import {
  type BulkItem,
  type BulkItemResult,
  settleEach,
} from 'src/utils/bulkResults';
import type {
  BulkRenameItem,
  BulkRenameResult,
} from 'switch-shared/modules/crud/base/base.dto';
import {
  CreateMeterPointDto,
  GetTaggedMeterPointsInput,
  GetTaggedMeterPointsOutput,
  GetZoneAccountsInput,
  GetZoneAccountsOutput,
  MeterPointCountSummaryInput,
  MeterPointCountSummaryOutput,
  SearchMeterPointInput,
  SearchMeterPointResultDto,
  UpdateMeterPointDto,
} from 'switch-shared/modules/crud/meter-point/meter-point.dto';
import { MeterPoint } from 'switch-shared/modules/crud/meter-point/meter-point.entity';

import { baseAPI, setToast } from './baseAPI.service';
import { API_METHODS, ENDPOINT_URLS } from './config/apiTags';

export const meterPointAPI = baseAPI
  .enhanceEndpoints({
    addTagTypes: [ENDPOINT_URLS.METER_POINT],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getMeterPointById: builder.query<MeterPoint, string>({
        query: (id) => `${ENDPOINT_URLS.METER_POINT}/${id}`,
        providesTags: (_res, _err, id) => [
          { type: ENDPOINT_URLS.METER_POINT, id },
        ],
      }),

      getAllMeterPoints: builder.query<
        SearchMeterPointResultDto,
        SearchMeterPointInput
      >({
        query: (params) => {
          return {
            url: `${ENDPOINT_URLS.METER_POINT}`,
            params,
          };
        },
        providesTags: (res, _err, _) =>
          res?.data.map((con) => ({
            type: ENDPOINT_URLS.METER_POINT,
            id: con._id,
          })) ?? [],
      }),

      getMeterPointsSummary: builder.query<
        MeterPointCountSummaryOutput,
        MeterPointCountSummaryInput
      >({
        query: (params) => {
          return {
            url: `${ENDPOINT_URLS.METER_POINT}/countSummary`,
            params,
          };
        },
      }),

      createMeterPoint: builder.mutation<MeterPoint, CreateMeterPointDto>({
        query: (data) => ({
          url: `${ENDPOINT_URLS.METER_POINT}`,
          method: API_METHODS.POST,
          body: data,
        }),
        invalidatesTags: [ENDPOINT_URLS.METER_POINT],
      }),

      updateMeterPoint: builder.mutation<
        MeterPoint,
        { id: string; data: UpdateMeterPointDto }
      >({
        query: ({ id, data }) => ({
          url: `${ENDPOINT_URLS.METER_POINT}/${id}`,
          method: API_METHODS.PATCH,
          body: data,
        }),
        invalidatesTags: (_res, _err, { id }) => [
          {
            type: ENDPOINT_URLS.METER_POINT,
            id,
          },
          { type: ENDPOINT_URLS.METER_POINT, id: 'TAGS' },
        ],
      }),

      getMeterPointTags: builder.query<
        string[],
        { applicationId?: string; applicationGroupId?: string }
      >({
        query: (params) => ({
          url: `${ENDPOINT_URLS.METER_POINT}/tags`,
          method: API_METHODS.GET,
          params,
        }),
        providesTags: [{ type: ENDPOINT_URLS.METER_POINT, id: 'TAGS' }],
      }),

      bulkRenameMeterPoints: builder.mutation<
        BulkRenameResult[],
        BulkRenameItem[]
      >({
        query: (items) => ({
          url: `${ENDPOINT_URLS.METER_POINT}/bulk-rename`,
          method: API_METHODS.PATCH,
          body: { items },
        }),
        invalidatesTags: (_res, _err, items) =>
          items.map(({ id }) => ({ type: ENDPOINT_URLS.METER_POINT, id })),
      }),

      /** Deletes each item with its own request, reporting per-item results. */
      bulkDeleteMeterPoints: builder.mutation<BulkItemResult[], BulkItem[]>({
        queryFn: async (items, _api, _extra, baseQuery) => ({
          data: await settleEach(items, ({ id }) =>
            baseQuery({
              url: `${ENDPOINT_URLS.METER_POINT}`,
              method: API_METHODS.DELETE,
              body: [id],
            })
          ),
        }),
        invalidatesTags: (_res, _err, items) =>
          items.map(({ id }) => ({ type: ENDPOINT_URLS.METER_POINT, id })),
      }),

      deleteMeterPoint: builder.mutation<{ success: boolean }, string[]>({
        query: (params) => ({
          url: `${ENDPOINT_URLS.METER_POINT}`,
          method: API_METHODS.DELETE,
          body: params,
        }),
        invalidatesTags: (_res, _err, ids) =>
          ids.map((id) => ({
            type: ENDPOINT_URLS.METER_POINT,
            id,
          })),
      }),

      getZoneAccounts: builder.query<
        GetZoneAccountsOutput,
        GetZoneAccountsInput
      >({
        query: (params) => ({
          url: `${ENDPOINT_URLS.METER_POINT}/getZoneAccounts`,
          method: API_METHODS.GET,
          params,
        }),
      }),

      getTaggedMeterPoints: builder.query<
        GetTaggedMeterPointsOutput,
        GetTaggedMeterPointsInput
      >({
        query: (params) => ({
          url: `${ENDPOINT_URLS.METER_POINT}/getTaggedMeterPoints`,
          method: API_METHODS.GET,
          params,
        }),
      }),
    }),
  });

export const {
  useGetMeterPointByIdQuery,
  useGetAllMeterPointsQuery,
  useGetMeterPointsSummaryQuery,
  useCreateMeterPointMutation,
  useUpdateMeterPointMutation,
  useBulkRenameMeterPointsMutation,
  useDeleteMeterPointMutation,
  useBulkDeleteMeterPointsMutation,
  useGetZoneAccountsQuery,
  useGetTaggedMeterPointsQuery,
  useGetMeterPointTagsQuery,
} = meterPointAPI;
export default meterPointAPI;

setToast('bulkRenameMeterPoints', {
  bulkResults: { noun: 'meter point', verb: 'rename' },
});
setToast('bulkDeleteMeterPoints', {
  bulkResults: { noun: 'meter point', verb: 'delete' },
});
